import axios from "axios";
import api from "../core/data/api";

/**
 * PUT file bytes to the presigned S3 URL using axios (not the shared `api` client).
 * The app `api` instance adds Bearer auth and baseURL — those would break S3 signatures.
 * Your GET `/download` route is only for reads; uploads must go to the signed S3 URL.
 */
async function putFileToPresignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string,
  signal?: AbortSignal
): Promise<void> {
  try {
    await axios.put(uploadUrl, file, {
      headers: { "Content-Type": contentType },
      signal,
      withCredentials: false,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  } catch (e: unknown) {
    const status =
      axios.isAxiosError(e) && e.response?.status != null
        ? e.response.status
        : "?";
    throw new Error(`S3 upload failed for ${file.name} (HTTP ${status})`);
  }
}

/** Mirrors POST /upload/sign rules */
export const MAX_IMAGE_FILES = 30;
/** Max size per image (KB-sized files pass; large phone JPEGs allowed) */
export const MAX_IMAGE_MB_PER_FILE = 25;
export const MAX_IMAGE_TOTAL_MB = 250;
export const MAX_IMAGE_BYTES_PER_FILE = MAX_IMAGE_MB_PER_FILE * 1024 * 1024;
export const MAX_IMAGE_TOTAL_BYTES = MAX_IMAGE_TOTAL_MB * 1024 * 1024;
export const ALLOWED_IMAGE_TYPES = new Set([
  // Images
  "image/jpeg",
  "image/png",
  "image/webp",

  // PDF
  "application/pdf",

  // Word
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",

  // Excel
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",

  // PowerPoint
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",

  // Text
  "text/plain",

  // Videos
  "video/mp4",
  "video/quicktime",
  "video/x-msvideo",
  "video/webm",

]);

const MAX_VIDEO_FILES = 30;
const MAX_VIDEO_BYTES_PER_FILE = 15 * 1024 * 1024;
const MAX_VIDEO_TOTAL_BYTES = 15 * 1024 * 1024;

export type SignedFileRow = {
  originalName: string;
  key: string;
  uploadUrl: string;
};

export type CompletedFileRow = {
  key: string;
  originalName: string;
  objectUrl: string;
};

/** Infer MIME when the browser leaves `file.type` empty (common on some platforms). */
export function resolveImageMimeType(file: File): string {
  const t = (file.type || "").trim();
  if (ALLOWED_IMAGE_TYPES.has(t)) return t;
  const n = file.name.toLowerCase();
  if (n.endsWith(".jpg") || n.endsWith(".jpeg")) return "image/jpeg";
  if (n.endsWith(".png")) return "image/png";
  if (n.endsWith(".webp")) return "image/webp";
  return t;
}

function validateImageFiles(files: File[]): string | null {
  if (files.length === 0) return "No files selected";
  if (files.length > MAX_IMAGE_FILES) {
    return `You can upload at most ${MAX_IMAGE_FILES} images`;
  }
  let total = 0;
  for (const f of files) {
    const mime = resolveImageMimeType(f);
    if (!ALLOWED_IMAGE_TYPES.has(mime)) {
      return `Not allowed: ${f.name} (${mime || "unknown type"}). Use JPEG, PNG, or WebP.`;
    }
    if (f.size > MAX_IMAGE_BYTES_PER_FILE) {
      return `Each image must be at most ${MAX_IMAGE_MB_PER_FILE} MB (${f.name})`;
    }
    total += f.size;
  }
  if (total > MAX_IMAGE_TOTAL_BYTES) {
    return `Total size of selected images must not exceed ${MAX_IMAGE_TOTAL_MB} MB`;
  }
  return null;
}

function resolveVideoMimeType(file: File): string {
  const t = (file.type || "").trim();
  if (t.startsWith("video/")) return t;
  const n = file.name.toLowerCase();
  if (n.endsWith(".mp4")) return "video/mp4";
  if (n.endsWith(".webm")) return "video/webm";
  if (n.endsWith(".mov")) return "video/quicktime";
  if (n.endsWith(".avi")) return "video/x-msvideo";
  if (n.endsWith(".mkv")) return "video/x-matroska";
  return t;
}

function validateVideoFiles(files: File[]): string | null {
  if (files.length === 0) return "No files selected";
  if (files.length > MAX_VIDEO_FILES) {
    return `You can upload at most ${MAX_VIDEO_FILES} videos`;
  }
  let total = 0;
  for (const f of files) {
    const mime = resolveVideoMimeType(f);
    if (!mime.startsWith("video/")) {
      return `Not a video file: ${f.name}`;
    }
    if (f.size > MAX_VIDEO_BYTES_PER_FILE) {
      return `Each video must be at most 15 MB (${f.name})`;
    }
    total += f.size;
  }
  if (total > MAX_VIDEO_TOTAL_BYTES) {
    return "Total size of selected videos must not exceed 15 MB";
  }
  return null;
}

/**
 * 1) Ask API for presigned PUT URLs (metadata only).
 * 2) Upload each file to S3 sequentially with axios.put (no FormData).
 * 3) Notify API /upload/complete for placeholder bookkeeping + canonical object URLs.
 */
export async function uploadImagesDirectToS3(
  files: File[],
  options?: { signal?: AbortSignal }
): Promise<{ objectUrls: string[]; keys: string[] }> {
  const msg = validateImageFiles(files);
  if (msg) throw new Error(msg);

  const signRes = await api.post<{ files?: SignedFileRow[] }>(
    "/upload/sign",
    {
      files: files.map((f) => ({
        name: f.name,
        type: resolveImageMimeType(f),
        size: f.size,
      })),
    },
    { signal: options?.signal }
  );

  const signed = signRes.data?.files;
  if (!Array.isArray(signed) || signed.length !== files.length) {
    throw new Error("Server did not return presigned URLs for all files.");
  }

  for (let i = 0; i < signed.length; i++) {
    const file = files[i];
    const row = signed[i];
    const contentType = resolveImageMimeType(file);
    await putFileToPresignedUrl(
      row.uploadUrl,
      file,
      contentType,
      options?.signal
    );
  }

  const complete = await api.post<{ files?: CompletedFileRow[] }>(
    "/upload/complete",
    {
      files: signed.map((s) => ({
        key: s.key,
        originalName: s.originalName,
      })),
    },
    { signal: options?.signal }
  );

  const done = complete.data?.files;
  if (!Array.isArray(done) || done.length !== signed.length) {
    throw new Error("Server did not acknowledge all uploads.");
  }
  return {
    objectUrls: done.map((f) => f.objectUrl),
    keys: done.map((f) => f.key),
  };
}

/** Same flow for POST /upload/sign-video (legacy video gallery limits). */
export async function uploadVideosDirectToS3(
  files: File[],
  options?: { signal?: AbortSignal }
): Promise<{ objectUrls: string[]; keys: string[] }> {
  const msg = validateVideoFiles(files);
  if (msg) throw new Error(msg);

  const signRes = await api.post<{ files?: SignedFileRow[] }>(
    "/upload/sign-video",
    {
      files: files.map((f) => ({
        name: f.name,
        type: resolveVideoMimeType(f),
        size: f.size,
      })),
    },
    { signal: options?.signal }
  );

  const signed = signRes.data?.files;
  if (!Array.isArray(signed) || signed.length !== files.length) {
    throw new Error("Server did not return presigned URLs for all files.");
  }

  for (let i = 0; i < signed.length; i++) {
    const file = files[i];
    const row = signed[i];
    const contentType = resolveVideoMimeType(file);
    await putFileToPresignedUrl(
      row.uploadUrl,
      file,
      contentType,
      options?.signal
    );
  }

  const complete = await api.post<{ files?: CompletedFileRow[] }>(
    "/upload/complete",
    {
      files: signed.map((s) => ({
        key: s.key,
        originalName: s.originalName,
      })),
    },
    { signal: options?.signal }
  );

  const done = complete.data?.files;
  if (!Array.isArray(done) || done.length !== signed.length) {
    throw new Error("Server did not acknowledge all uploads.");
  }
  return {
    objectUrls: done.map((f) => f.objectUrl),
    keys: done.map((f) => f.key),
  };
}

/** Single image helper for forms that only upload one photo. */
export async function uploadOneImageDirectToS3(
  file: File,
  options?: { signal?: AbortSignal }
): Promise<string> {
  const { objectUrls } = await uploadImagesDirectToS3([file], options);
  return objectUrls[0];
}
